import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { contextApi } from "@/api/context";
import { useAuth } from "@/auth/AuthProvider";
import type { LeagueDto, TournamentDto } from "@/types/api";

interface AppContextValue {
  leagues: LeagueDto[];
  tournaments: TournamentDto[];
  leagueId: string | null;
  tournamentId: string | null;
  currentLeague: LeagueDto | null;
  currentTournament: TournamentDto | null;
  setLeagueId: (id: string) => void;
  setTournamentId: (id: string) => void;
  isLoading: boolean;
  /** Van-e legalább egy liga + torna kiválasztva (a scope-olt oldalak ezt várják). */
  isReady: boolean;
  error: unknown;
}

const AppContext = createContext<AppContextValue | null>(null);

const LS_LEAGUE = "tj.leagueId";
const LS_TOURNAMENT = "tj.tournamentId";

export function AppContextProvider({ children }: { children: ReactNode }) {
  const { isAuthenticated } = useAuth();

  const leaguesQ = useQuery({
    queryKey: ["leagues"],
    queryFn: contextApi.leagues,
    enabled: isAuthenticated,
  });
  const tournamentsQ = useQuery({
    queryKey: ["tournaments"],
    queryFn: contextApi.tournaments,
    enabled: isAuthenticated,
  });

  const leagues = useMemo(() => leaguesQ.data ?? [], [leaguesQ.data]);
  const tournaments = useMemo(() => tournamentsQ.data ?? [], [tournamentsQ.data]);

  const [leagueId, setLeagueIdState] = useState<string | null>(() => localStorage.getItem(LS_LEAGUE));
  const [tournamentId, setTournamentIdState] = useState<string | null>(() => localStorage.getItem(LS_TOURNAMENT));

  const setLeagueId = (id: string) => {
    localStorage.setItem(LS_LEAGUE, id);
    setLeagueIdState(id);
  };
  const setTournamentId = (id: string) => {
    localStorage.setItem(LS_TOURNAMENT, id);
    setTournamentIdState(id);
  };

  // Alapértelmezett kiválasztás, ha még nincs / a korábbi már nem elérhető.
  useEffect(() => {
    if (leagues.length && (!leagueId || !leagues.some((l) => l.id === leagueId))) {
      setLeagueId(leagues[0].id);
    }
  }, [leagues, leagueId]);

  useEffect(() => {
    if (tournaments.length && (!tournamentId || !tournaments.some((t) => t.id === tournamentId))) {
      setTournamentId(tournaments[0].id);
    }
  }, [tournaments, tournamentId]);

  const currentLeague = leagues.find((l) => l.id === leagueId) ?? null;
  const currentTournament = tournaments.find((t) => t.id === tournamentId) ?? null;

  const value: AppContextValue = {
    leagues,
    tournaments,
    leagueId,
    tournamentId,
    currentLeague,
    currentTournament,
    setLeagueId,
    setTournamentId,
    isLoading: leaguesQ.isLoading || tournamentsQ.isLoading,
    isReady: !!currentLeague && !!currentTournament,
    error: leaguesQ.error ?? tournamentsQ.error,
  };

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useAppContext(): AppContextValue {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error("useAppContext csak <AppContextProvider>-en belül használható.");
  return ctx;
}
